import React, { useEffect, useMemo, useState } from "react";
import { Download, X } from "lucide-react";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function isStandaloneMode() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari
    (window.navigator as any).standalone === true
  );
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

function isMobileDevice() {
  return (
    window.matchMedia("(pointer: coarse)").matches ||
    /android|iphone|ipad|ipod/i.test(window.navigator.userAgent)
  );
}

const DISMISS_KEY = "pwa_install_banner_dismissed_at";
const DISMISS_DAYS = 7;

export default function InstallPwaPrompt() {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [hidden, setHidden] = useState(false);

  const shouldShow = useMemo(() => {
    if (hidden) return false;
    if (!isMobileDevice()) return false;
    if (isStandaloneMode()) return false;

    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const ts = Number(dismissedAt);
      if (!Number.isNaN(ts)) {
        const days = (Date.now() - ts) / (1000 * 60 * 60 * 24);
        if (days < DISMISS_DAYS) return false;
      }
    }

    // iOS: no beforeinstallprompt; show instructions banner
    if (isIOS()) return true;

    // Android/Chromium: only show if install event is available
    return deferredPrompt !== null;
  }, [deferredPrompt, hidden]);

  useEffect(() => {
    const onBeforeInstallPrompt = (e: Event) => {
      // Prevent the mini-infobar
      e.preventDefault?.();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    const onAppInstalled = () => {
      setDeferredPrompt(null);
      setHidden(true);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  if (!shouldShow) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setHidden(true);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    try {
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") {
        setHidden(true);
      }
    } finally {
      setDeferredPrompt(null);
    }
  };

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[60]">
      <div className="max-w-2xl mx-auto bg-white border border-zinc-200 shadow-xl rounded-2xl p-4 flex items-start gap-3">
        <div className="shrink-0 p-2 rounded-xl bg-blue-50 text-[#3f83f8]">
          <Download className="w-5 h-5" />
        </div>

        <div className="flex-1">
          <div className="font-black text-zinc-900 leading-tight">
            Instalar Pochete.App
          </div>
          {isIOS() ? (
            <div className="text-sm text-zinc-600 mt-1">
              No iPhone/iPad: toque em <b>Compartilhar</b> e depois{" "}
              <b>Adicionar à Tela de Início</b>.
            </div>
          ) : (
            <div className="text-sm text-zinc-600 mt-1">
              Instale como app no seu celular para acesso rápido.
            </div>
          )}

          <div className="mt-3 flex items-center gap-2">
            {!isIOS() && (
              <button
                onClick={install}
                className="px-4 py-2 rounded-xl bg-[#3f83f8] hover:bg-blue-600 text-white font-bold text-sm"
              >
                Instalar
              </button>
            )}
            <button
              onClick={dismiss}
              className="px-4 py-2 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-bold text-sm"
            >
              Agora não
            </button>
          </div>
        </div>

        <button
          onClick={dismiss}
          className="shrink-0 p-2 rounded-xl hover:bg-zinc-100 text-zinc-500"
          aria-label="Fechar"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
